import { Box, FormikTextInput, TextInput } from '@palmetto/palmetto-components';
import { FieldArray, useFormikContext } from 'formik';
import { ChangeEvent, useCallback } from 'react';

import { MonthNamesShort } from 'types';

interface TwelveMonthsConsumptionInputProps {
  isLoading: boolean;
  fieldName: string;
  min?: number;
  max?: number;
  suffix?: string;
}

export const TwelveMonthsConsumptionInput = ({
  isLoading,
  fieldName,
  min = 0,
  max = Number.MAX_VALUE,
  suffix = 'kWh',
}: TwelveMonthsConsumptionInputProps) => {
  const { isSubmitting, setFieldValue, values } = useFormikContext<any>();
  const isAnythingLoadingOrSubmitting = isLoading || isSubmitting;

  const setValue = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      if (value === '') {
        setFieldValue(event.target.name, '');
        return;
      }

      const numericValue = Number(value);
      if (!isNaN(numericValue) && numericValue >= min && numericValue <= max) {
        setFieldValue(event.target.name, numericValue);
        setFieldValue('electricityUsageUpdated', true);
      }
    },
    [max, min, setFieldValue],
  );

  const handleBlur = useCallback(() => {
    const totalElectricityUsageKwh = (values.electricityUsageKwh || []).reduce(
      (acc: number, usage: number) => acc + (usage || 0),
      0,
    );
    setFieldValue('totalElectricityUsageKwh', totalElectricityUsageKwh);
  }, [setFieldValue, values.electricityUsageKwh]);

  return (
    <Box
      childGap={{ base: 'lg', desktop: 'xl' }}
      display="grid"
      style={{
        gridTemplateColumns: 'repeat(3,minmax(0,1fr))',
        columnGap: 'var(--size-spacing-sm)',
        rowGap: 'var(--size-spacing-sm)',
      }}
    >
      <FieldArray
        name={fieldName}
        render={() =>
          MonthNamesShort.map((month: string, index: number) => (
            <Box key={index}>
              <TextInput
                label={month}
                name={`${fieldName}[${index}]`}
                id={`${fieldName}-${month}`}
                component={FormikTextInput}
                isDisabled={isAnythingLoadingOrSubmitting}
                onChange={setValue}
                suffix={suffix}
                type="number"
                onBlur={handleBlur}
                value={values[fieldName]?.[index] ?? ''}
              />
            </Box>
          ))
        }
      />
    </Box>
  );
};
